import { useQuery } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'

export interface ProviderField {
  key: string
  label: string
  type: string
  sampleValue?: string
  suggestedType?: string  // Only on extension fields
}

export interface AvailableFieldsResponse {
  standard: ProviderField[]
  extension: ProviderField[]
  totalExtensionFields: number
}

type RawAvailableFieldsResponse =
  | AvailableFieldsResponse
  | {
      data?: AvailableFieldsResponse
      standard?: ProviderField[]
      extension?: ProviderField[]
      totalExtensionFields?: number
    }

function normalizeAvailableFields(raw: RawAvailableFieldsResponse): AvailableFieldsResponse {
  const source =
    raw && typeof raw === 'object' && 'data' in raw && raw.data
      ? raw.data
      : raw

  return {
    standard: Array.isArray(source?.standard) ? source.standard : [],
    extension: Array.isArray(source?.extension) ? source.extension : [],
    totalExtensionFields:
      typeof source?.totalExtensionFields === 'number'
        ? source.totalExtensionFields
        : Array.isArray(source?.extension)
        ? source.extension.length
        : 0,
  }
}

export function useProviderFields(provider: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['provider-fields', provider],
    queryFn: async () => {
      const raw = await fetchApi<RawAvailableFieldsResponse>(`/integrations/${provider}/available-fields`)
      return normalizeAvailableFields(raw)
    },
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour (matches backend cache)
  })
}
